import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button, Icon, Skeleton } from "@nexus/ui";
import { useIsSuperTenant } from "@/features/auth/hooks/usePermissions";
import type { EventsChartLatencyMetric } from "@/features/admin/lib/events-chart-metric";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { getUserFacingApiMessage, passThroughMessage } from "@/lib/api-client";
import {
  eventActionsInfiniteQueryOptions,
  eventsInfiniteQueryOptions,
  eventsStatsQueryOptions,
  eventsTimeseriesQueryOptions,
  eventsUsersQueryOptions,
  usePublishEvent,
  type EventActionItem,
  type EventJobItem,
  type EventJobSortBy,
  type EventJobStatus,
} from "@/lib/queries";
import { formatEventActivityRangeLabel } from "../lib/event-activity-series";
import { hasUnfinishedEventActions } from "../lib/event-action-display";
import { hasActiveProcessingEventJobs } from "../lib/event-processing-display";
import {
  buildEventStatusOptions,
  buildEventTopicOptions,
  EVENT_FILTER_ALL,
  type EventFilterOption,
} from "../constants/event-filter-options";
import { eventTimeRangeHours, type EventTimeRange } from "../constants/event-time-ranges";
import { EventActionDetailDrawer } from "./EventActionDetailDrawer";
import { EventActionsTable } from "./EventActionsTable";
import { EventDetailDrawer } from "./EventDetailDrawer";
import { EventPublishSheet } from "./EventPublishSheet";
import { EventsActivityChart, type EventActivityRange } from "./EventsActivityChart";
import { EventsEmptyState } from "./EventsEmptyState";
import { EventsFilterBar } from "./EventsFilterBar";
import { EventsKpiStrip } from "./EventsKpiStrip";
import { EventsTable } from "./EventsTable";
import { EventsViewToggle, type EventsView } from "./EventsViewToggle";

export interface EventsViewFilters {
  /** Whether a row is one published event or one user action's whole fan-out. */
  view: EventsView;
  status?: EventJobStatus;
  eventType?: string;
  topic?: string;
  actorUserId?: string;
  /** Free-text match against correlationId or eventType. */
  q?: string;
  hidePings: boolean;
  /** Rolling window scoping the table, the KPI strip and the chart alike. */
  range: EventTimeRange;
  /** Set by clicking a bucket in the activity chart; ISO, both bounds inclusive. */
  publishedFrom?: string;
  publishedTo?: string;
  sortBy: EventJobSortBy;
  sortOrder: "asc" | "desc";
}

interface EventsPageProps {
  filters: EventsViewFilters;
  onFiltersChange: (next: EventsViewFilters) => void;
  /** Live PROCESSING duration past this reads as timed out. From the ops settings. */
  stuckAfterMs: number;
  /** An unfinished action older than this reads as overdue. From the ops settings. */
  slowAfterMs: number;
  /** Which latency the Activity chart plots. From the ops settings. */
  chartLatencyMetric: EventsChartLatencyMetric;
  /** Whether the chart draws `slowAfterMs` as a threshold. From the ops settings. */
  showChartBudgetLine: boolean;
}

export const EVENTS_PAGE_SIZE = 50;
/** Processing rows advance visibly, so poll while any are in flight. */
const PROCESSING_POLL_INTERVAL_MS = 3_000;
const DURATION_TICK_INTERVAL_MS = 1_000;

function flattenPages<TItem>(pages: Array<{ items: TItem[] }> | undefined): TItem[] {
  if (!pages) return [];
  return pages.flatMap((page) => page.items);
}

export function EventsPage({
  filters,
  onFiltersChange,
  stuckAfterMs,
  slowAfterMs,
  chartLatencyMetric,
  showChartBudgetLine,
}: EventsPageProps) {
  const { t, i18n } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  const [selectedEvent, setSelectedEvent] = useState<EventJobItem | null>(null);
  const [selectedAction, setSelectedAction] = useState<EventActionItem | null>(null);
  const [isPublishOpen, setIsPublishOpen] = useState(false);
  /** Set only by republish, so the publish sheet opens blank the rest of the time. */
  const [republishFrom, setRepublishFrom] = useState<EventJobItem | null>(null);
  const isSuperTenant = useIsSuperTenant();
  const publish = usePublishEvent();

  const windowHours = eventTimeRangeHours(filters.range);

  const { data: usersData } = useQuery(eventsUsersQueryOptions());
  const {
    data: stats,
    isLoading: isStatsLoading,
    // True while the strip is still showing the previous window's numbers.
    isPlaceholderData: isStatsStale,
  } = useQuery(eventsStatsQueryOptions(windowHours));

  // Status is the chart's stacked series and the bucket selection is what the
  // chart sets, so neither narrows it — everything else does, which keeps the
  // chart describing the same slice of history as the table under it.
  const { data: activity, isLoading: isActivityLoading, isPlaceholderData: isActivityPlaceholder } =
    useQuery(
      eventsTimeseriesQueryOptions({
        eventTypeIn: filters.eventType ? [filters.eventType] : undefined,
        topic: filters.topic || undefined,
        actorUserId: filters.actorUserId || undefined,
        q: filters.q || undefined,
        hidePings: filters.hidePings,
        windowHours,
      }),
    );

  const queryFilters = useMemo(
    () => ({
      statusIn: filters.status ? [filters.status] : undefined,
      eventTypeIn: filters.eventType ? [filters.eventType] : undefined,
      topic: filters.topic || undefined,
      actorUserId: filters.actorUserId || undefined,
      q: filters.q || undefined,
      hidePings: filters.hidePings,
      windowHours,
      publishedFrom: filters.publishedFrom,
      publishedTo: filters.publishedTo,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
      limit: EVENTS_PAGE_SIZE,
    }),
    [filters, windowHours],
  );

  const isActionsView = filters.view === "actions";

  // The grouped endpoint ignores both, and carrying them would add a cache key
  // for a narrowing that never happens.
  const actionFilters = useMemo(() => {
    const { statusIn: _statusIn, sortBy: _sortBy, ...rest } = queryFilters;
    return rest;
  }, [queryFilters]);

  const eventsQuery = useInfiniteQuery({
    ...eventsInfiniteQueryOptions(queryFilters),
    enabled: !isActionsView,
    refetchInterval: (query) => {
      const pages = query.state.data?.pages;
      if (!pages) return false;
      const hasProcessing = pages.some((page) =>
        page.items.some((row) => row.status === "PROCESSING"),
      );
      return hasProcessing ? PROCESSING_POLL_INTERVAL_MS : false;
    },
  });

  const actionsQuery = useInfiniteQuery({
    ...eventActionsInfiniteQueryOptions(actionFilters),
    enabled: isActionsView,
    refetchInterval: (query) => {
      const pages = query.state.data?.pages;
      if (!pages) return false;
      return pages.some((page) => hasUnfinishedEventActions(page.items))
        ? PROCESSING_POLL_INTERVAL_MS
        : false;
    },
  });

  const {
    isLoading,
    isError,
    isFetching,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = isActionsView ? actionsQuery : eventsQuery;

  const rows = useMemo(
    () => flattenPages<EventJobItem>(eventsQuery.data?.pages),
    [eventsQuery.data?.pages],
  );
  const actionRows = useMemo(
    () => flattenPages<EventActionItem>(actionsQuery.data?.pages),
    [actionsQuery.data?.pages],
  );
  const rowCount = isActionsView ? actionRows.length : rows.length;
  const totalCount =
    (isActionsView ? actionsQuery.data : eventsQuery.data)?.pages[0]?.totalCount ?? null;

  const userOptions = useMemo<EventFilterOption[]>(() => {
    const byId = new Map<string, string>();
    for (const user of usersData ?? []) byId.set(user.id, user.displayName);
    for (const row of rows) {
      if (row.actorUserId && !byId.has(row.actorUserId)) {
        byId.set(row.actorUserId, row.actorUserName ?? row.actorUserId);
      }
    }
    return [
      { value: EVENT_FILTER_ALL, label: t("admin.events.filters.user", "All users") },
      ...[...byId.entries()].map(([id, name]) => ({ value: id, label: name })),
    ];
  }, [usersData, rows, t]);

  const statusOptions = useMemo(() => buildEventStatusOptions(t), [t]);
  const topicOptions = useMemo(() => buildEventTopicOptions(t), [t]);

  // An action's total climbs until every one of its events has landed, so the
  // actions view ticks on whether the group is finished rather than on the
  // status of any one row.
  const hasActiveProcessingRows = useMemo(
    () =>
      isActionsView
        ? hasUnfinishedEventActions(actionRows)
        : hasActiveProcessingEventJobs(rows, now, stuckAfterMs),
    [isActionsView, actionRows, rows, now, stuckAfterMs],
  );

  useEffect(() => {
    if (!hasActiveProcessingRows) return;
    const id = setInterval(() => setNow(Date.now()), DURATION_TICK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hasActiveProcessingRows]);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useInfiniteScroll(
    sentinelRef,
    scrollContainerRef,
    () => void fetchNextPage(),
    !!hasNextPage,
    isFetchingNextPage,
    { enabled: !isLoading },
  );

  const onSortPublished = useCallback(() => {
    onFiltersChange({
      ...filters,
      sortBy: "publishedAt",
      sortOrder:
        filters.sortBy === "publishedAt" && filters.sortOrder === "desc" ? "asc" : "desc",
    });
  }, [filters, onFiltersChange]);

  const hasActiveFilters =
    !!filters.status ||
    !!filters.eventType ||
    !!filters.topic ||
    !!filters.actorUserId ||
    !!filters.q ||
    !!filters.publishedFrom;

  // The window survives a clear, like `hidePings` does: both are how the admin
  // has chosen to look at the panel, not a narrowing they are trying to undo.
  const onClearFilters = useCallback(() => {
    onFiltersChange({
      view: filters.view,
      hidePings: filters.hidePings,
      range: filters.range,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    });
  }, [
    filters.view,
    filters.hidePings,
    filters.range,
    filters.sortBy,
    filters.sortOrder,
    onFiltersChange,
  ]);

  // Status belongs to a single event, so it cannot survive the switch to a view
  // whose rows are groups.
  const onViewChange = useCallback(
    (view: EventsView) => {
      setSelectedAction(null);
      onFiltersChange({ ...filters, view, status: view === "actions" ? undefined : filters.status });
    },
    [filters, onFiltersChange],
  );

  const onSelectAction = useCallback((action: EventActionItem) => {
    setSelectedAction(action);
  }, []);

  const onShowActionEvents = useCallback(
    (action: EventActionItem) => {
      setSelectedAction(null);
      onFiltersChange({
        ...filters,
        view: "events",
        status: undefined,
        q: action.correlationId,
        hidePings: false,
      });
    },
    [filters, onFiltersChange],
  );

  const onSelectActivityRange = useCallback(
    (range: EventActivityRange | null) => {
      onFiltersChange({
        ...filters,
        publishedFrom: range?.from,
        publishedTo: range?.to,
      });
    },
    [filters, onFiltersChange],
  );

  const onTimeRangeChange = useCallback(
    (range: EventTimeRange) => {
      // The bucket selection is an absolute slice of the old window and the new
      // one may not contain it — or may not have a bar at that boundary — so
      // widening or narrowing drops it rather than leaving a filter behind that
      // the chart can no longer show.
      onFiltersChange({
        ...filters,
        range,
        publishedFrom: undefined,
        publishedTo: undefined,
      });
    },
    [filters, onFiltersChange],
  );

  const selectedRange = useMemo(
    () =>
      filters.publishedFrom && filters.publishedTo
        ? { from: filters.publishedFrom, to: filters.publishedTo }
        : null,
    [filters.publishedFrom, filters.publishedTo],
  );

  const rangeLabel = selectedRange
    ? formatEventActivityRangeLabel(selectedRange.from, selectedRange.to, i18n.language)
    : null;

  const onPublish = useCallback(
    (input: { eventName: string; payload: Record<string, unknown> }) => {
      publish.mutate(
        { ...input, idempotencyKey: crypto.randomUUID() },
        {
          onSuccess: (result) => {
            setIsPublishOpen(false);
            setRepublishFrom(null);
            // A published event lands among hundreds of others, so the toast
            // offers the one thing that finds it again: its correlation ID.
            toast.success(t("admin.events.publish.sent", "Event published"), {
              action: {
                label: t("admin.events.publish.follow", "Show it"),
                onClick: () =>
                  onFiltersChange({
                    ...filters,
                    q: result.correlationId,
                    hidePings: false,
                  }),
              },
            });
          },
        },
      );
    },
    [publish, filters, onFiltersChange, t],
  );

  const onRepublish = useCallback((event: EventJobItem) => {
    // Two stacked sheets would trap focus in the wrong one, so the drawer
    // steps aside for the sheet that is about to do the work.
    setSelectedEvent(null);
    setSelectedAction(null);
    setRepublishFrom(event);
    setIsPublishOpen(true);
  }, []);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <EventsKpiStrip
        stats={stats}
        isLoading={isStatsLoading}
        isStale={isStatsStale}
        timeRange={filters.range}
        onTimeRangeChange={onTimeRangeChange}
        isFailedFilterActive={filters.status === "FAILED"}
        onFilterFailed={() => onFiltersChange({ ...filters, status: "FAILED" })}
      />

      <EventsActivityChart
        data={activity}
        isLoading={isActivityLoading}
        isPlaceholderData={isActivityPlaceholder}
        latencyMetric={chartLatencyMetric}
        budgetMs={showChartBudgetLine ? slowAfterMs : null}
        windowHours={windowHours}
        selectedRange={selectedRange}
        onSelectRange={onSelectActivityRange}
      />

      <EventsFilterBar
        search={filters.q ?? ""}
        onSearchChange={(value) => onFiltersChange({ ...filters, q: value || undefined })}
        status={filters.status ?? EVENT_FILTER_ALL}
        onStatusChange={(value) =>
          onFiltersChange({
            ...filters,
            status: value === EVENT_FILTER_ALL ? undefined : (value as EventJobStatus),
          })
        }
        statusOptions={statusOptions}
        showStatusFilter={!isActionsView}
        topic={filters.topic ?? EVENT_FILTER_ALL}
        onTopicChange={(value) =>
          onFiltersChange({
            ...filters,
            topic: value === EVENT_FILTER_ALL ? undefined : value,
          })
        }
        topicOptions={topicOptions}
        actorUserId={filters.actorUserId ?? EVENT_FILTER_ALL}
        onActorUserChange={(value) =>
          onFiltersChange({
            ...filters,
            actorUserId: value === EVENT_FILTER_ALL ? undefined : value,
          })
        }
        userOptions={userOptions}
        hidePings={filters.hidePings}
        onHidePingsChange={(value) => onFiltersChange({ ...filters, hidePings: value })}
        healthPingCount={stats?.healthPings}
        rangeLabel={rangeLabel}
        onClearRange={() => onSelectActivityRange(null)}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={onClearFilters}
        resultCount={totalCount}
        resultUnit={isActionsView ? "actions" : "events"}
        isRefreshing={isFetching && !isFetchingNextPage}
        onRefresh={() => void refetch()}
        actions={
          <>
            <EventsViewToggle view={filters.view} onViewChange={onViewChange} />
            {/* Platform admins only: a published event runs with the worker's
                authority, which is not something an org can be granted. */}
            {isSuperTenant ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 shrink-0 rounded-sm text-xs"
                onClick={() => {
                  setRepublishFrom(null);
                  setIsPublishOpen(true);
                }}
              >
                <Icon name="terminal" size="xs" aria-hidden />
                {t("admin.events.publish.open", "Publish event")}
              </Button>
            ) : null}
          </>
        }
      />

      <div
        ref={scrollContainerRef}
        className="min-h-0 flex-1 overflow-auto rounded-md border border-border bg-card"
      >
        {isLoading ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : isError ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-8">
            <p className="text-sm text-muted-foreground">
              {t("admin.events.loadError", "Failed to load events.")}
            </p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {t("admin.events.retry", "Retry")}
            </Button>
          </div>
        ) : rowCount === 0 ? (
          <EventsEmptyState
            timeRange={filters.range}
            onTimeRangeChange={onTimeRangeChange}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={onClearFilters}
          />
        ) : (
          <div>
            {isActionsView ? (
              <EventActionsTable
                rows={actionRows}
                now={now}
                slowAfterMs={slowAfterMs}
                onSelectRow={onSelectAction}
                selectedCorrelationId={selectedAction?.correlationId ?? null}
              />
            ) : (
              <EventsTable
                rows={rows}
                now={now}
                stuckAfterMs={stuckAfterMs}
                durationScaleMs={stats?.p95RunMs ?? null}
                sortBy={filters.sortBy}
                sortOrder={filters.sortOrder}
                onSortPublished={onSortPublished}
                onSelectRow={setSelectedEvent}
                selectedRowId={selectedEvent?.id ?? null}
              />
            )}
            <div
              ref={sentinelRef}
              className="flex items-center justify-center p-4 text-xs text-muted-foreground"
            >
              {isFetchingNextPage ? (
                <span className="inline-flex items-center gap-2">
                  <Icon
                    name="loader"
                    size="xs"
                    className="animate-spin motion-reduce:animate-none"
                    aria-hidden
                  />
                  {t("admin.events.loadingMore", "Loading more…")}
                </span>
              ) : hasNextPage ? (
                <Button variant="ghost" size="sm" onClick={() => void fetchNextPage()}>
                  {t("admin.events.loadMore", "Load more")}
                </Button>
              ) : (
                <span>{t("admin.events.endOfResults", "End of results")}</span>
              )}
            </div>
          </div>
        )}
      </div>

      <EventActionDetailDrawer
        action={selectedAction}
        now={now}
        stuckAfterMs={stuckAfterMs}
        windowHours={windowHours}
        onClose={() => setSelectedAction(null)}
        onShowEvents={onShowActionEvents}
        onRepublish={isSuperTenant ? onRepublish : undefined}
      />

      <EventDetailDrawer
        event={selectedEvent}
        now={now}
        stuckAfterMs={stuckAfterMs}
        onClose={() => setSelectedEvent(null)}
        onRepublish={isSuperTenant ? onRepublish : undefined}
      />

      {isSuperTenant ? (
        <EventPublishSheet
          isOpen={isPublishOpen}
          isPublishing={publish.isPending}
          publishError={publish.error ? getUserFacingApiMessage(publish.error, passThroughMessage) : null}
          onOpenChange={(open) => {
            setIsPublishOpen(open);
            if (!open) {
              publish.reset();
              setRepublishFrom(null);
            }
          }}
          onPublish={onPublish}
          initialEventName={republishFrom?.eventType}
          initialPayload={republishFrom?.payload}
        />
      ) : null}
    </div>
  );
}
